import React, { ComponentRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Carousel, Col, Modal, Row } from "react-bootstrap";
import { FaYoutube } from "react-icons/fa";
import { BsCircleFill } from "react-icons/bs";
import PrismaZoom from 'react-prismazoom'
import { Nullable } from "../../../definitions/types";
import YouTube, { YouTubeProps } from 'react-youtube'
import { useScreenSizeDetector } from "../../../hooks/useScreenSizeDetector";
import { Thumbnail } from "./thumbnail";
import { PlayButton } from "./playButton";
import { useMediaGalleryQuery } from "../../../hooks/useMediaGalleryQuery";
import { YouTubePlayButton } from "./youTubePlayButton";
import { useAppDispatch, useMediaGallery } from "../../../redux/stateHooks";
import { mediaGalleryActions } from "../../../redux/slices/mediaGallery";
import { GALLERY_IDENTITY, isDevelopmentBuild } from "../../../utils/constants";
import { useHash } from "../../../hooks/useHash";

const opts: YouTubeProps['opts'] = {
    playerVars: { autoplay: 1, height: '390', width: '640' }
};
function formatDuration(durationInSeconds: number) {
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = Math.floor(durationInSeconds % 60);

    return `${padNumber(hours)}:${padNumber(minutes)}:${padNumber(seconds)}`;
}

function padNumber(number: number) {
    return String(number).padStart(2, '0');
}


export const Gallery = () => {
    const dispatch = useAppDispatch();
    const {
        media: {
            internalCount,
            images: {
                internal: images,
            },
            videos: {
                internal: videos,
                external: youtubeVideos,
                externalCount: youtubeCount
            }
        }
    } = useMediaGalleryQuery();
    const { isLargeScreenSize } = useScreenSizeDetector();
    const { update: updateHash } = useHash();
    const { activeIndex, modal: { show, zoom, showZoomHint } } = useMediaGallery();

    const [isYoutubePlayingArray, setIsYouTubePlayingArray] = useState<boolean[]>(new Array(youtubeCount).fill(false));
    const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
    const [hasVideoPlayed, setHasVideoPlayed] = useState(false);
    const [lastPercentage, setLastPercentage] = useState(0);

    const [currentPlaying, setCurrentPlaying] = useState<{
        videoId: number | null;
        videoTitle: unknown;
        index: number | null;
        isYouTube: boolean;
    }>({ videoId: null, index: null, isYouTube: false, videoTitle: null });
    const containerRef = useRef<HTMLDivElement>(null);
    const prismaZoomRefs = useRef<Array<ComponentRef<typeof PrismaZoom> | null>>(new Array(internalCount).fill(null));
    const videoItemRef = useRef<Array<Nullable<HTMLVideoElement>>>(new Array(internalCount).fill(null));
    const youtubeRefs: Array<YouTube | null> = useMemo(() => new Array(youtubeCount).fill(null), [youtubeVideos]);

    useEffect(() => {
        if (!containerRef.current) return;
        const selectedImage = containerRef.current.querySelector('.active-thumbnail') as HTMLElement;
        if (!selectedImage) return;
        containerRef.current.scrollTo({
            left: selectedImage.offsetLeft - containerRef.current.clientWidth / 2 + selectedImage.clientWidth / 2,
            behavior: 'smooth'
        });
    }, [activeIndex]);

    const pushToDataLayer = (eventAction: string, videoElement: HTMLVideoElement) => {
        window.dataLayer.push({
            'event': 'video',
            'eventAction': eventAction,
            'eventLabel': 'ID: ' + currentPlaying.videoId + ' (' + currentPlaying.videoTitle + ')',
            'eventValue': formatDuration(videoElement.currentTime),
            'videoLength': formatDuration(videoElement.duration),
        });
    };

    useEffect(() => {

        const handleVideoPlayed = () => {
            // Check if currentPlaying.index is not null and video has not played yet
            if (currentPlaying.index !== null && !hasVideoPlayed) {
                const videoElement = videoItemRef.current[currentPlaying.index];
                // Check if the pause is due to seeking
                if (videoElement && !videoElement.seeking) {
                    pushToDataLayer('Play', videoElement);
                    // Set the state to indicate that the video has been played
                    setHasVideoPlayed(true);
                }
            }
        };

        const handleVideoPaused = () => {
            
            // Check if currentPlaying.index is not null and reset hasVideoPlayed
            if (currentPlaying.index !== null) {
                setHasVideoPlayed(false);

                const videoElement = videoItemRef.current[currentPlaying.index];
                
                if (videoElement) {
                    if (Math.abs(videoElement.currentTime - videoElement.duration) < .05) return;

                    // Check if the pause is due to seeking
                    if (!videoElement.seeking) {
                        pushToDataLayer('Pause', videoElement);
                    }
                }
            }
        };

        const handleVideoEnded = () => {
            setIsVideoPlaying(false);

            // Check if currentPlaying.index is not null and video has not played yet
            if (currentPlaying.index !== null && !hasVideoPlayed) {
                const videoElement = videoItemRef.current[currentPlaying.index];

                if (videoElement) { 
                    
                    pushToDataLayer('Ended', videoElement);

                    setHasVideoPlayed(true);
                }
            }
        };

        let lastUpdateTime = 0;

        const handleTimeUpdate = (event: Event) => {
            const videoElement = event.target as HTMLVideoElement;

            if (videoElement.currentTime >= videoElement.duration) {
       
                pushToDataLayer('Watched 100%', videoElement);

                handleVideoEnded();
                
            } else if (isVideoPlaying && videoElement.currentTime < videoElement.duration) {
                handleVideoPlayed();

                // Calculate the current percentage
                const percentageWatched = Math.floor((videoElement.currentTime / videoElement.duration) * 100);


                // Check if the percentage is a multiple of 10 and has increased
                if (percentageWatched % 10 === 0 && percentageWatched > lastPercentage) {
                    const currentTime = new Date().getTime();

                    // Check if at least 2 seconds have passed since the last update
                    if (currentTime - lastUpdateTime >= 2000) {
                         
                        pushToDataLayer(`Watched ${percentageWatched}%`, videoElement);

                        // Update the last recorded percentage
                        setLastPercentage(percentageWatched);
                    }

                    // Always update the last update time
                    lastUpdateTime = currentTime;
                }
            }
        };

        videoItemRef.current.forEach((videoElement) => {
            if (videoElement) {
                videoElement.addEventListener("timeupdate", handleTimeUpdate);
                videoElement.addEventListener("ended", handleVideoEnded);
                videoElement.addEventListener("pause", handleVideoPaused);
            }
        });

        return () => {
            videoItemRef.current.forEach((videoElement) => {
                if (videoElement) {
                    videoElement.removeEventListener("timeupdate", handleTimeUpdate);
                    videoElement.removeEventListener("ended", handleVideoEnded);
                    videoElement.removeEventListener("pause", handleVideoPaused);
                }
            });
        };
    }, [isVideoPlaying, videoItemRef, currentPlaying, hasVideoPlayed]);



    useEffect(() => {
        // If showZoomHint is false or zoomLevel is not 1, return (no need to reset zoom)
        if (!showZoomHint || zoom !== 1) return;

        // Set timeout to hide zoom hint after 6 seconds (6000 milliseconds)
        const timeout = setTimeout(() => dispatch(mediaGalleryActions.modal.hideZoomHint()), 6000);

        // Return a function to clear the timeout when component unmounts (cleanup)
        return () => clearTimeout(timeout);
    }, [showZoomHint, zoom]);

    const onZoomChange = useCallback((zoom: number) => {
        if (zoom < 1 || zoom > 5) throw new Error('Zoom level must be between 1 and 5 inclusive');
        dispatch(mediaGalleryActions.modal.setZoom(zoom as 1 | 2 | 3 | 4 | 5));
        dispatch(mediaGalleryActions.modal.hideZoomHint())
    }, [])

    const handleImageClick = (index: number) => {
        dispatch(mediaGalleryActions.setActiveIndex(index));
        dispatch(mediaGalleryActions.modal.show());
        updateHash(GALLERY_IDENTITY);
    };

    const handleModalClose = () => {
        pauseCurrentMedia();
        dispatch(mediaGalleryActions.modal.hide());
    };

    const onThumbnailClick = (index: number) => {
        dispatch(mediaGalleryActions.setActiveIndex(index));
        pauseCurrentMedia();
        handleReset();
    };

    const handleCarouselSelect = (index: number) => {
        dispatch(mediaGalleryActions.setActiveIndex(index));
        handleReset();
        pauseCurrentMedia();
    };

    const handleReset = () => {
        if (!prismaZoomRefs.current[activeIndex]) return;
        prismaZoomRefs.current[activeIndex]?.reset();
    };

    const toggleIsVideoPlaying = (index: number) => {
        if (currentPlaying.index === null || currentPlaying.index === index) return;
        pauseCurrentMedia();
    };

    const handlePlayClick = (videoId: number, index: number, videoTitle: string) => {
        // Initialize dataLayer if it doesn't exist
        window.dataLayer = window.dataLayer || [];
        pauseCurrentMedia();
        setCurrentPlaying({ videoId: videoId, index: index, isYouTube: false, videoTitle: videoTitle }); // Update the current playing state
        setIsVideoPlaying(true);
        //video plays on auto play once the video replaces the poster image when isvideoplaying is true

    };

    const handleYouTubePlayClick = (index: number) => {
        pauseCurrentMedia();

        // Check if the clicked YouTube video is already playing
        if (isYoutubePlayingArray[index]) {
            // Pause the YouTube video
            if (youtubeRefs[index]) youtubeRefs[index]?.internalPlayer.pauseVideo();
            setIsYouTubePlayingArray(new Array(youtubeCount).fill(false));
            setIsVideoPlaying(false);
        } else {
            // Play the new YouTube video
            const newIsVideoPlayingArray = new Array(youtubeCount).fill(false);
            newIsVideoPlayingArray[index] = true;

            // Update the current playing state after checking if video is already playing
            setCurrentPlaying({ videoId: null, index: index, isYouTube: true, videoTitle: null });
            setIsVideoPlaying(false);
            setIsYouTubePlayingArray(newIsVideoPlayingArray);
        }
    };


    // Pause the currently playing media (if any)
    const pauseCurrentMedia = () => {
        setIsVideoPlaying(false);
        setHasVideoPlayed(false);

        if (currentPlaying.index === null) return;

        if (currentPlaying.isYouTube) {
            if (youtubeRefs[currentPlaying.index]) {
                youtubeRefs[currentPlaying.index]?.internalPlayer.pauseVideo();
            }
        } else {
            const videoElement = videoItemRef.current[currentPlaying.index];

            if (videoElement) {

                videoElement.pause();
            }
        }

        setCurrentPlaying({ videoId: null, index: null, isYouTube: false, videoTitle: null }); // Update the current playing state
        setIsVideoPlaying(false);
        setIsYouTubePlayingArray(new Array(internalCount).fill(false));
    };

    const hasYouTubeVideos = youtubeVideos.length > 0;

    return (
        <Row id={GALLERY_IDENTITY}>
            <Col className="p-0">
                {!show && (
                    <>
                        <Carousel
                            onSlide={toggleIsVideoPlaying}
                            id="product-gallery"
                            className="d-flex align-items-center"
                            indicators={false}
                            interval={null}
                            touch={true}
                            draggable={false}
                            variant="dark"
                            onSelect={handleCarouselSelect}
                            activeIndex={activeIndex}
                            controls={(images.length + videos.length + youtubeVideos.length) > 1}
                            wrap={true}
                        >
                            {images.map(({ previewUrl, alt, title, height, width }, index) => (
                                <Carousel.Item key={index}>
                                    <div className="image-container">
                                        <img className="d-block img-fluid"
                                            onClick={() => handleImageClick(index)}
                                            src={previewUrl}
                                            alt={alt}
                                            title={title}
                                            height={height && height}
                                            width={width && width}
                                            loading={index === 0 ? "eager" : "lazy"}
                                            decoding={index === 0 ? "sync" : "async"}
                                            aria-hidden
                                        />
                                    </div>
                                </Carousel.Item>
                            ))}
                            {videos.map((video, index) => (
                                <Carousel.Item key={index}>
                                    <div className="image-container">
                                        {(currentPlaying.index === index && isVideoPlaying) ? (
                                            <div className="w-100">
                                                <video
                                                    key={index}
                                                    ref={(ref: HTMLVideoElement | null) => videoItemRef.current[index] = ref}
                                                    controls
                                                    src={video.url}
                                                    className="d-block img-fluid"
                                                    autoPlay={isVideoPlaying}
                                                    width={444}
                                                    height={250} />
                                            </div>
                                        ) : (
                                            <PlayButton
                                                key={index}
                                                model={video}
                                                onClick={() => handlePlayClick(video.id ?? 'ID is Missing.', index, video.title ?? 'Title is Missing.')}
                                            />
                                        )}
                                    </div>
                                </Carousel.Item>
                            ))}
                            {hasYouTubeVideos &&
                                youtubeVideos.map((model, index) => (
                                    <Carousel.Item key={index}>
                                        <div className="image-container">
                                            {(isYoutubePlayingArray[index]) ? (
                                                <div className="youtube-image-container">
                                                    <YouTube
                                                        videoId={model.id}
                                                        opts={opts}
                                                        ref={(ref: YouTube | null) => youtubeRefs[index] = ref} />
                                                </div>
                                            ) : (
                                                <YouTubePlayButton key={index}
                                                    model={model}
                                                    onClick={() => handleYouTubePlayClick(index)} />
                                            )}
                                        </div>
                                    </Carousel.Item>
                                ))
                            }
                        </Carousel>
                        {(isLargeScreenSize) ? (
                            <div className="flex-wrap d-flex mt-2" style={{ height: '136px' }}>
                                {[...images, ...videos].map((image, index) => (
                                    <Thumbnail key={index}
                                        model={image}
                                        isActive={activeIndex === index}
                                        onClick={() => onThumbnailClick(index)} />
                                ))}
                                {hasYouTubeVideos && (
                                    youtubeVideos.map(({ thumbnailCollection: { defaultResolution: { url } } }, index) => (
                                        <div key={index}
                                            onClick={() => onThumbnailClick(internalCount + index)}
                                            className={`${activeIndex === (internalCount + index) ? 'active-thumbnail' : 'inactive-thumbnail'} position-relative text-center overlay-thumb-wrap mx-1 my-1`}>
                                            <img src={url}
                                                alt="YouTube Thumbnail"
                                                className="p-1"
                                                height={86}
                                                width={86}
                                                loading="lazy"
                                                decoding="async" 
                                                aria-hidden/>
                                            <div className="position-absolute top-50 start-50 translate-middle ">
                                                <FaYoutube
                                                    aria-label="Play Youtube Video"
                                                    size={20}
                                                    style={{ cursor: 'pointer', color: '#FF0000' }}
                                                    className="text-danger"
                                                />
                                                <div
                                                    className="bg-white position-absolute top-50 start-50 translate-middle"
                                                    style={{ height: '10px', width: '10px', zIndex: '-1' }} />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )
                            : (
                                <>
                                    <ul className="custom-carousel-indicators carousel-indicators d-flex flex-wrap m-0 mt-2"
                                        style={{ listStyle: 'none' }}
                                        role="tablist"
                                        aria-label="Image indicators">
                                        {[...images, ...videos].map((_, index) => (
                                            <li
                                                key={index}
                                                role="tab"
                                                tabIndex={0}
                                                onClick={() => onThumbnailClick(index)}
                                                className={`${activeIndex === index && 'active'} mx-1`}
                                                style={{ cursor: 'pointer' }}>
                                                <BsCircleFill
                                                    className={`${activeIndex === index ? 'text-primary' : 'text-muted opacity-50'}`}
                                                    size={12}
                                                    role="presentation" />
                                            </li>
                                        ))}
                                        {hasYouTubeVideos && (
                                            youtubeVideos.map((_, index) => (
                                                <li role="tab"
                                                    key={internalCount + index}
                                                    tabIndex={0}
                                                    onClick={() => onThumbnailClick(internalCount + index)}
                                                    className={`${activeIndex === (internalCount + index) && 'active'} mx-1`}
                                                    style={{ cursor: 'pointer' }}>
                                                    <BsCircleFill
                                                        className={activeIndex === (internalCount + index) ? 'text-primary' : 'text-muted opacity-50'}
                                                        size={12}
                                                        role="presentation" />
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                </>
                            )
                        }
                    </>
                )}
                <Modal id="product-gallery-overlay"
                    show={show}
                    onHide={handleModalClose}
                    dialogClassName="large-modal-dialog"
                    fullscreen
                    scrollable={false}>
                    <Modal.Header>
                        <Button variant="primary"
                            onClick={handleModalClose}
                            className="me-3"
                            aria-label="Close Media Gallery Overlay">
                            Back
                        </Button>
                    </Modal.Header>
                    <Modal.Body className="position-relative">
                        <Carousel variant="dark"
                            activeIndex={activeIndex}
                            onSlide={() => toggleIsVideoPlaying(activeIndex)}
                            onSelect={handleCarouselSelect}
                            interval={null}
                            indicators={false}
                            touch={zoom === 1}
                            controls={(images.length + videos.length + youtubeVideos.length) > 1}
                            draggable={false}
                            wrap={true}>
                            {images.map((image, index) => (
                                <Carousel.Item key={index}>
                                    <>
                                        {image.description && (
                                            <Carousel.Caption className="text-muted">
                                                {image.description}
                                            </Carousel.Caption>
                                        )}
                                        <PrismaZoom key={index}
                                            ref={(ref: any) => prismaZoomRefs.current[index] = ref}
                                            className="zoom-container"
                                            maxZoom={!isLargeScreenSize ? 5 : 2}
                                            minZoom={1}
                                            allowParentPanning={zoom === 1}
                                            onZoomChange={onZoomChange}
                                        >
                                            <img style={{
                                                maxHeight: '100%',
                                                maxWidth: '100%',
                                                margin: "auto",
                                                cursor: zoom === 1 ? 'zoom-in' : 'move'
                                            }}
                                                src={image.zoomUrl}
                                                alt={image.alt}
                                                title={image.title}
                                                className="py-lg-5"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </PrismaZoom>
                                        {showZoomHint && zoom === 1 && (
                                            <div id="modal-zoom-hint">
                                                Double {!isLargeScreenSize ? 'Tap or Pinch to Zoom' : 'Click Image to Toggle Zoom'}
                                            </div>
                                        )}
                                    </>
                                </Carousel.Item>
                            ))}
                            {videos.map((video, index) => (
                                <Carousel.Item key={index}>
                                    {(currentPlaying.index === index && isVideoPlaying) ? (
                                        <div className="w-100">
                                            <video key={index}
                                                ref={(ref: HTMLVideoElement | null) => videoItemRef.current[index] = ref}
                                                controls
                                                src={video.url}
                                                className="d-block img-fluid"
                                                autoPlay={isVideoPlaying}
                                                width={1280}
                                                height={720} />
                                        </div>
                                    ) : (
                                        <PlayButton key={index}
                                            model={video}
                                            onClick={() => handlePlayClick(video.id ?? 'Default ID', index, video.title ?? 'Default Title')}
                                        />
                                    )}
                                </Carousel.Item>
                            ))}
                            {hasYouTubeVideos && (
                                youtubeVideos.map((model, index) => (
                                    <Carousel.Item key={index}>
                                        <>
                                            {(isYoutubePlayingArray[index]) ? (
                                                <div className="youtube-image-container">
                                                    <YouTube videoId={model.id}
                                                        opts={opts}
                                                        ref={(ref: YouTube | null) => youtubeRefs[index] = ref} />
                                                </div>
                                            ) : (
                                                <YouTubePlayButton key={index}
                                                    model={model}
                                                    onClick={() => handleYouTubePlayClick(index)} />
                                            )}
                                        </>
                                    </Carousel.Item>
                                ))
                            )}
                        </Carousel>
                        <div
                            className={`${(!isLargeScreenSize) && 'position-absolute bottom-0'} ${(isLargeScreenSize) && internalCount > 10 && 'scrollable-container'}`}
                            ref={containerRef}>
                            <div className="flex-wrap d-flex mt-3 justify-content-center">
                                {[...images, ...videos].map((image, index) => (
                                    <Thumbnail key={index}
                                        model={image}
                                        isActive={activeIndex === index}
                                        onClick={() => onThumbnailClick(index)} />
                                ))}
                                {hasYouTubeVideos && (
                                    youtubeVideos.map(({ thumbnailCollection: { defaultResolution: { url } } }, index) => (
                                        <div key={index}
                                            onClick={() => onThumbnailClick(internalCount + index)}
                                            className={`${activeIndex === (internalCount + index) ? 'active-thumbnail' : 'inactive-thumbnail'} position-relative text-center overlay-thumb-wrap mx-1 my-1`}>
                                            <img src={url} alt="YouTube Thumbnail" className="p-1" loading="lazy" decoding="async" />
                                            <div className="position-absolute top-50 start-50 translate-middle">
                                                <FaYoutube
                                                    aria-label="Play Youtube Video"
                                                    size={20}
                                                    style={{ cursor: 'pointer', color: '#FF0000' }}
                                                    className="text-danger" />
                                                <div
                                                    className="bg-white position-absolute top-50 start-50 translate-middle"
                                                    style={{ height: '10px', width: '10px', zIndex: '-1' }} />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            </Col>
        </Row>
    );
};